import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';
import { formatPower, formatCurrency, formatDistance, formatEnergy } from '../lib/locale';

export default function StopDetailPage() {
  const { stopId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locale, countryCode } = useLocale();
  const tripId = searchParams.get('trip');
  
  const [station, setStation] = useState(null);
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const { data: stationData, error: sErr } = await supabase
          .from('stations')
          .select('*')
          .eq('station_id', stopId)
          .single();
        if (sErr) throw sErr;
        setStation(stationData);
        
        if (stationData?.network && stationData?.country_code) {
          const { data: networkData } = await supabase
            .from('networks')
            .select('*')
            .eq('network_name', stationData.network)
            .eq('market_code', stationData.country_code)
            .maybeSingle();
          setNetwork(networkData);
        }
        
        setLoading(false);
      } catch (e) {
        setError(e.message);
        setLoading(false);
      }
    }
    load();
  }, [stopId]);
  
  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Loading stop" backTo={tripId ? `/result/${tripId}` : '/'} />
        <div className="loading-state">Loading station details…</div>
      </div>
    );
  }
  
  if (error || !station) {
    return (
      <div className="page">
        <PageHeader title="Stop not found" backTo={tripId ? `/result/${tripId}` : '/'} />
        <div className="error-banner">{error || 'No station data'}</div>
      </div>
    );
  }
  
  const helplineUrl = `https://pluginany.com/?tab=safety&country=${countryCode}`;
  
  return (
    <div className="page">
      <PageHeader 
        title="Charging stop"
        backTo={tripId ? `/result/${tripId}` : '/'}
      />
      
      <div style={{
        background: 'linear-gradient(135deg, rgba(13, 31, 24, 1) 0%, rgba(5, 5, 5, 1) 100%)',
        borderRadius: 14,
        padding: 16,
        marginBottom: 12,
      }}>
        <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>
          {station.name || 'Charging station'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
          {station.address || `${station.city || ''}, ${station.state_or_province || ''}`}
          {station.altitude_m != null && ` · ${formatDistance(station.altitude_m / 1000, locale)}`}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {station.network && (
            <span style={{
              fontSize: 11,
              padding: '3px 8px',
              borderRadius: 5,
              background: 'rgba(108, 181, 255, 0.15)',
              color: 'var(--accent-info)',
              fontWeight: 500,
            }}>
              {station.network}
            </span>
          )}
          {station.peak_dc_kw && (
            <span style={{
              fontSize: 11,
              padding: '3px 8px',
              borderRadius: 5,
              background: 'rgba(255, 184, 77, 0.15)',
              color: 'var(--accent-warning)',
              fontWeight: 500,
            }}>
              {formatPower(station.peak_dc_kw)}
            </span>
          )}
          {station.data_quality && (
            <span style={{
              fontSize: 11,
              padding: '3px 8px',
              borderRadius: 5,
              background: 'rgba(32, 217, 166, 0.15)',
              color: 'var(--accent-primary)',
              fontWeight: 500,
            }}>
              ✓ {station.data_quality.replace('Verified-', '')}
            </span>
          )}
        </div>
      </div>
      
      <Section title="Charger specs">
        {station.peak_dc_kw && <Row label="Peak power" value={formatPower(station.peak_dc_kw)} />}
        {station.peak_ac_kw && <Row label="AC power" value={formatPower(station.peak_ac_kw)} />}
        {station.dc_connectors?.length > 0 && (
          <Row label="DC connectors" value={station.dc_connectors.join(', ')} />
        )}
        {station.ac_connectors?.length > 0 && (
          <Row label="AC connectors" value={station.ac_connectors.join(', ')} />
        )}
        {station.port_count_dc != null && <Row label="DC ports" value={station.port_count_dc} />}
        {station.port_count_ac != null && <Row label="AC ports" value={station.port_count_ac} />}
      </Section>
      
      {network && (
        <Section title="Network & payment">
          {network.required_app && <Row label="Required app" value={network.required_app} />}
          {network.payment_methods && <Row label="Payment" value={network.payment_methods} />}
          {network.typical_tariff_local && (
            <Row 
              label="Typical tariff"
              value={`${formatCurrency(network.typical_tariff_local, locale)} / kWh`}
            />
          )}
          {network.support_number && (
            <Row label="Support" value={network.support_number} />
          )}
        </Section>
      )}
      
      {station.access && (
        <Section title="Access">
          <Row label="Access" value={station.access} />
          {station.hours_open && <Row label="Hours" value={station.hours_open} />}
          {station.hours_24_7 && <Row label="Hours" value="24/7" />}
        </Section>
      )}
      
      <a 
        href={helplineUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'rgba(255, 87, 87, 0.08)',
          border: '0.5px solid rgba(255, 87, 87, 0.3)',
          borderRadius: 12,
          padding: '12px 14px',
          fontSize: 12,
          color: '#ff8a8a',
          textDecoration: 'none',
          marginTop: 12,
        }}
      >
        <span style={{ color: 'var(--accent-danger)', fontSize: 16 }}>📞</span>
        <span>Need help? Open Safety & Helplines for {locale.country_name}</span>
      </a>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      borderRadius: 10,
      padding: '12px 14px',
      marginBottom: 8,
      border: '0.5px solid var(--border-subtle)',
    }}>
      <div style={{
        fontSize: 10,
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: 8,
        fontWeight: 500,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 12,
      padding: '5px 0',
    }}>
      <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}
